// Web Component: RecentlyViewed
// Descrizione: carosello dei prodotti visti di recente dal visitatore. Lo storico vive in localStorage
// (handle dei prodotti, il più recente in testa) ed è scritto su ogni PDP da theme-scripts con
// RecentlyViewed.add(). Le card arrivano dalla Section Rendering API (helper-card), come in
// CustomCarousel: il Liquid della product-card è riusato così com'è.
// Attributi accettati:
//   - data-limit: numero massimo di card da mostrare (default 10)
//   - data-current: handle del prodotto corrente, escluso dal carosello (solo in PDP)
// Struttura richiesta: un <template data-template="carousel"> figlio con un elemento [data-products]
// che riceve le slide.
// Eventi emessi: nessuno
// Dipendenze: Shopify.routes.root, sezione helper-card, theme-swiper
// Limiti: storico per dispositivo e per browser; senza localStorage (es. bloccato) il carosello non
// compare.
// Utilizzo: sections/section-recently-viewed.liquid
class RecentlyViewed extends HTMLElement {
  static KEY = 'pittarello.recentlyViewed';
  static MAX = 12;

  observer; template;

  connectedCallback() {
    this.template = this.querySelector('template[data-template="carousel"]');
    const handles = this.getHandles();
    // Storico vuoto: il componente resta com'è. In vetrina contiene solo il <template>, che non si
    // vede; in editor contiene il segnaposto reso dal Liquid.
    if (!this.template || handles.length === 0) return;

    // Le card si chiedono solo quando la sezione si avvicina al viewport: in PDP il carosello sta
    // in fondo e i suoi fetch non devono competere con il caricamento della pagina.
    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.observer.disconnect();
      this.render(handles);
    }, { rootMargin: '400px 0px' });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  getHandles = () => {
    const limit = parseInt(this.dataset.limit) || 10;
    return RecentlyViewed.getList()
      .filter((handle) => handle !== this.dataset.current)
      .slice(0, limit);
  }

  // Una richiesta per card, in parallelo. allSettled e non all (come in CustomCarousel): un handle
  // che non esiste più toglie la sua card, non l'intero carosello.
  render = async (handles) => {
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const results = await Promise.allSettled(handles.map((handle) => {
      return fetch(`${root}products/${encodeURIComponent(handle)}?section_id=helper-card`).then((response) => {
        if (response.ok) return response.text();
        // 404 = prodotto eliminato, non pubblicato o rinominato: inutile richiederlo ancora.
        if (response.status === 404) RecentlyViewed.forget(handle);
        throw new Error(`RecentlyViewed: ${handle} → ${response.status}`);
      });
    }));
    const cards = results
      .filter((result) => result.status === 'fulfilled')
      .map((result) => result.value.trim())
      .filter((html) => html !== '');
    if (cards.length === 0) return;

    // Le slide entrano nel wrapper PRIMA che il template arrivi nel DOM: theme-swiper si
    // inizializza nel connectedCallback e così trova già tutte le card.
    const content = this.template.content.cloneNode(true);
    const wrapper = content.querySelector('[data-products]');
    if (!wrapper) return;
    wrapper.innerHTML = cards.join('');
    this.replaceChildren(content);
  }

  static getList() {
    try {
      const list = JSON.parse(localStorage.getItem(RecentlyViewed.KEY));
      return Array.isArray(list) ? list.filter((handle) => typeof handle === 'string' && handle !== '') : [];
    } catch (e) {
      return [];
    }
  }

  static save(list) {
    try {
      localStorage.setItem(RecentlyViewed.KEY, JSON.stringify(list.slice(0, RecentlyViewed.MAX)));
    } catch (e) {
      // localStorage pieno o bloccato: lo storico semplicemente non si aggiorna.
    }
  }

  // Il più recente in testa; se già presente torna in cima invece di duplicarsi.
  static add(handle) {
    if (!handle) return;
    const list = RecentlyViewed.getList().filter((h) => h !== handle);
    list.unshift(handle);
    RecentlyViewed.save(list);
  }

  static forget(handle) {
    RecentlyViewed.save(RecentlyViewed.getList().filter((h) => h !== handle));
  }
}
if (!customElements.get('recently-viewed')) {
  customElements.define('recently-viewed', RecentlyViewed);
}
